import { useState } from "react";
import Icon from "@/components/icon";
import { VitrinCategory } from "@/models/home/VitrinCategory";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";

interface FilterSectionProps {
  categories: VitrinCategory[];
  selectedCategoryIds: number[];
  sortOrder: string;
  searchQuery: string;
  onCategoryClick: (categoryId: number) => void;
  onSortChange: (sort: string) => void;
  onSearchChange: (query: string) => void;
  loading?: boolean;
}

/** Search ranking. Asked for by omitting `sort`, not by sending this value. */
export const RELEVANCE_SORT = 'relevance';

/** The order to browse the shelf in when there is no query to rank against. */
export const DEFAULT_SORT = '-created_at';

const sortOptions = [
  { value: RELEVANCE_SORT, label: 'مرتبط‌ترین', icon: 'search' },
  { value: DEFAULT_SORT, label: 'جدیدترین ها', icon: 'ascending-list' },
  { value: 'created_at', label: 'قدیمی‌ترین ها', icon: 'descending-list' },
  { value: '-price', label: 'گران‌ترین', icon: 'ascending-list' },
  { value: 'price', label: 'ارزان‌ترین', icon: 'descending-list' },
];

function getSortLabel(value: string): string {
  return sortOptions.find(option => option.value === value)?.label || 'مرتب‌سازی';
}

function FilterSection({
  categories,
  selectedCategoryIds,
  sortOrder,
  searchQuery,
  onCategoryClick,
  onSortChange,
  onSearchChange,
  loading = false,
}: FilterSectionProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <section className="px-4 space-y-3 text-sm md:px-6">
      {/* A search field stretched across a desktop rail reads as a mistake. */}
      <div className="relative md:max-w-md">
        <input
          type="text"
          placeholder="جستجو"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full rounded-full bg-background border border-default-100 p-2 pr-4 text-sm text-default-800 placeholder:text-default-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/70 transition"
        />

        <span className="absolute top-1/2 left-3 -translate-y-1/2 text-default-400">
          {loading ?
            <div className="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
            :
            <Icon name="search" size={20} />
          }
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerTrigger asChild>
            <button
              type="button"
              className="px-4 py-2 rounded-full text-nowrap text-xs font-medium border transition text-default-600 border-default-300 bg-background flex items-center gap-2 flex-shrink-0"
            >
              <Icon name="sort" size={16} />
              {getSortLabel(sortOrder)}
            </button>
          </DrawerTrigger>
          <DrawerContent className="rounded-t-[10px] mt-24">
            <h2 className="font-semibold mb-4 text-sm text-default-800">مرتب‌سازی</h2>
            <div className="space-y-2">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onSortChange(option.value);
                    setDrawerOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 text-right p-3 rounded-lg text-xs font-medium border transition ${sortOrder === option.value
                    ? 'text-primary border-primary bg-primary/10'
                    : 'text-default-600 border-default-300 bg-background'
                    }`}
                >
                  <Icon name={option.icon} size={16} />
                  {option.label}
                </button>
              ))}
            </div>
          </DrawerContent>
        </Drawer>
        <div className="flex-1 min-w-0">
          <Swiper
            spaceBetween={8}
            slidesPerView="auto"
            modules={[FreeMode]}
            freeMode={{
              enabled: true,
              sticky: false,
              momentum: true,
              momentumRatio: 1,
              momentumVelocityRatio: 1,
              minimumVelocity: 0.02,
            }}
            className="!m-0"
          >
            {categories.map((category) => (
              <SwiperSlide key={category.id} className="!w-auto">
                <button
                  type="button"
                  onClick={() => onCategoryClick(category.id)}
                  className={`px-4 py-2 rounded-full text-xs font-medium border transition whitespace-nowrap ${selectedCategoryIds.includes(category.id)
                    ? 'text-primary border-primary bg-primary/10'
                    : 'text-default-600 border-default-300 bg-background'
                    }`}
                >
                  {category.name}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
}

export default FilterSection;
